<script setup>
import { computed, ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCommunitiesStore } from '@/stores/communitiesStore'
import { useFrontEndStore } from '@/stores/frontEndStore'
import { useMeteringPointMembershipsStore } from '@/stores/meteringPointMembershipsStore'

const { t } = useI18n()
const communitiesStore = useCommunitiesStore()
const frontEndStore = useFrontEndStore()
const mpmStore = useMeteringPointMembershipsStore()

const localeToUse = ref("de-AT")
const billingPeriodDateStart2023 = ref(new Date(2023, 1 - 1, 1))
const billingPeriodDateEnd2023 = ref(new Date(2023, 12 - 1, 31))
const billingPeriodDateStart2024 = ref(new Date(2024, 1 - 1, 1))
const billingPeriodDateEnd2024 = ref(new Date(2024, 12 - 1, 31))
const billingPeriodDateEnd2025 = ref(new Date(2025, 12 - 1, 31))

const loading = ref(false)

const communitiesAvailable = computed(() => {
  return communitiesStore.allCommunities.map(c => ({
    title: c.name,
    props: { subtitle: c.ecn },
    ecn: c.ecn,
    ecid: c.ecid
  }))
})

const selectedEcnList = computed({
  get: () => frontEndStore.meteringPointAnalysisFilter.ecnList,
  set: (val) => { frontEndStore.meteringPointAnalysisFilter.ecnList = val }
})

const billingAnalysisReport = computed(() => {
  let cr = []

  const filteredCommunities = selectedEcnList.value.length
    ? communitiesStore.allCommunities.filter(c => selectedEcnList.value.includes(c.ecn))
    : communitiesStore.allCommunities

  for (const c of filteredCommunities) {
    const mpms = mpmStore.allMeteringPointMemberships.filter(mpm => mpm.ecid === c.ecid)
    let mpmsCalced2023 = []
    let mpmsCalced2024 = []
    let mpmMonth2023 = 0
    let mpmMonth2024 = 0

    // memberships 2023 post calc (Energieabhängiges Modell)
    for (const mpm of mpms) {
      if (['CREATED', 'STARTED', 'DECLINED', 'WAITING_FOR_USER', 'ACCEPTED', 'ABORTED'].includes(mpm.membershipStatus)) continue

      const mpmDateStart = new Date(mpm.dateStart)
      const mpmDateEnd = mpm.dateEnd ? new Date(mpm.dateEnd) : null

      if (mpmDateEnd && mpmDateEnd < billingPeriodDateStart2023.value) continue
      if (mpmDateStart > billingPeriodDateEnd2023.value) continue

      let monthStart = 1
      let monthEnd = 12
      if (mpmDateStart > billingPeriodDateStart2023.value) monthStart = mpmDateStart.getMonth() + 1
      if (mpmDateEnd && mpmDateEnd < billingPeriodDateEnd2023.value) monthEnd = mpmDateEnd.getMonth() + 1

      const countMonth = monthEnd - monthStart + 1
      mpmMonth2023 += countMonth
      mpmsCalced2023.push({ ...mpm, countMonth })
    }

    // memberships 2024 post calc (Energieabhängiges Modell)
    for (const mpm of mpms) {
      if (['CREATED', 'STARTED', 'DECLINED', 'WAITING_FOR_USER', 'ACCEPTED', 'ABORTED'].includes(mpm.membershipStatus)) continue

      const mpmDateStart = new Date(mpm.dateStart)
      const mpmDateEnd = mpm.dateEnd ? new Date(mpm.dateEnd) : null

      if (mpmDateEnd && mpmDateEnd < billingPeriodDateStart2024.value) continue
      if (mpmDateStart > billingPeriodDateEnd2024.value) continue

      let monthStart = 1
      let monthEnd = 12
      if (mpmDateStart > billingPeriodDateStart2024.value) monthStart = mpmDateStart.getMonth() + 1
      if (mpmDateEnd && mpmDateEnd < billingPeriodDateEnd2024.value) monthEnd = mpmDateEnd.getMonth() + 1

      const countMonth = monthEnd - monthStart + 1
      mpmMonth2024 += countMonth
      mpmsCalced2024.push({ ...mpm, countMonth })
    }

    // membership calc 2024 (Pauschalmodell)
    let md = []
    let dateCurrent = new Date(billingPeriodDateStart2024.value)
    while (dateCurrent <= billingPeriodDateEnd2025.value) {
      const dStart = new Date(dateCurrent)
      let dEnd = new Date(dStart)
      dEnd.setMonth(dEnd.getMonth() + 1)
      dEnd.setDate(0)

      let mpsAccountedFor = []
      let mpsMultiple = []
      let countMpmActive = 0
      let countMpmActivated = 0
      let countMpmDeactivated = 0
      let countMpmActiveDouble = 0

      for (const mpm of mpms) {
        if (['CREATED', 'STARTED', 'DECLINED', 'WAITING_FOR_USER', 'ACCEPTED', 'ABORTED'].includes(mpm.membershipStatus)) continue

        const mpmDateStart = new Date(mpm.dateStart)
        const mpmDateEnd = mpm.dateEnd ? new Date(mpm.dateEnd) : null

        if (mpmDateEnd && mpmDateEnd < dStart) continue
        if (mpmDateStart > dEnd) continue

        if (!mpsAccountedFor.includes(mpm.meteringPoint)) {
          countMpmActive++
          mpsAccountedFor.push(mpm.meteringPoint)
        } else {
          countMpmActiveDouble++
          mpsMultiple.push(mpm.meteringPoint)
        }
        if (mpmDateStart >= dStart) countMpmActivated++
        if (mpmDateEnd && mpmDateEnd <= dEnd) countMpmDeactivated++
      }

      md.push({
        timespan: dStart.toLocaleDateString(localeToUse.value) + " > " + dEnd.toLocaleDateString(localeToUse.value),
        countMpmActive, countMpmActivated, countMpmDeactivated, countMpmActiveDouble, mpsMultiple
      })
      dateCurrent.setMonth(dateCurrent.getMonth() + 1)
    }

    cr.push({
      ...c,
      mpmsAll: mpms,
      mpms2023: mpmsCalced2023,
      mpms2024: mpmsCalced2024,
      mpmMonth2023,
      mpmMonth2024,
      monthData: md
    })
  }
  return cr
})

onMounted(async () => {
  loading.value = true
  try {
    await Promise.all([
      communitiesStore.fetchCommunities(),
      mpmStore.fetchMeteringPointMemberships()
    ])
    // Select all communities by default if none selected
    if (!selectedEcnList.value.length) {
      selectedEcnList.value = communitiesStore.allCommunities.map(c => c.ecn)
    }
  } finally {
    loading.value = false
  }
})
</script>

<script>
export default {
  name: 'meteringPointsAnalysis',
}
</script>

<template>
  <v-container class="containerBase pa-0">
    <v-container class="containerStandard">
      <p class="textHeadline">{{ $t('analysisPage.billing') }}</p>

      <v-autocomplete
        class="mb-4"
        bg-color="white"
        :label="t('community')"
        v-model="selectedEcnList"
        :items="communitiesAvailable"
        :filter-keys="['title', 'props.subtitle']"
        multiple
        variant="outlined"
        item-value="ecn"
        item-title="title"
        clearable
        hide-details
      >
        <template v-slot:item="{ props, item }">
          <v-list-item v-bind="props" :title="item.raw.title" :subtitle="item.raw.props.subtitle" />
        </template>
      </v-autocomplete>

      <div v-if="loading" class="d-flex justify-center align-center pa-8">
        <v-progress-circular indeterminate color="primary" size="64" />
      </div>

      <template v-else>
        <v-alert type="info" class="mb-4" variant="tonal">
          <p class="font-weight-bold mb-1">{{ t('analysisPage.billingRulesTitle') }}</p>
          {{ t('analysisPage.billingRulesDescription') }}
        </v-alert>

        <div class="mb-4 text-subtitle-2">
          {{ t('from') }}: {{ billingPeriodDateStart2023.toLocaleDateString(localeToUse) }}<br>
          {{ t('to') }}: {{ billingPeriodDateEnd2025.toLocaleDateString(localeToUse) }}
        </div>

        <div v-for="community in billingAnalysisReport" :key="community.ecn" class="mb-8 pa-4 border rounded shadow-sm bg-white">
          <div class="d-flex justify-space-between align-center mb-4 border-bottom pb-2">
            <div>
              <h2 class="text-h5 font-weight-bold">{{ community.name }}</h2>
              <p class="text-caption text-grey">{{ community.ecn }} / {{ community.ecid }}</p>
            </div>
          </div>

          <v-row>
            <v-col cols="12" md="6">
              <v-card variant="outlined" class="pa-3 fill-height">
                <p class="text-subtitle-2 border-bottom mb-2 pb-1">{{ t('analysisPage.statusSummary') }}</p>
                <div class="d-flex flex-wrap ga-2">
                  <v-chip size="small" variant="flat">All: {{ community.mpmsAll.length }}</v-chip>
                  <v-chip size="small" color="success" variant="flat">Active: {{ community.mpmsAll.filter(m => m.membershipStatus === 'ACTIVE' || m.membershipStatus === 'ENDING').length }}</v-chip>
                  <v-chip size="small" color="error" variant="flat">Declined: {{ community.mpmsAll.filter(m => m.membershipStatus === 'DECLINED').length }}</v-chip>
                  <v-chip size="small" color="warning" variant="flat">Waiting: {{ community.mpmsAll.filter(m => m.membershipStatus === 'WAITING_FOR_USER').length }}</v-chip>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card variant="outlined" class="pa-3 fill-height">
                <p class="text-subtitle-2 border-bottom mb-2 pb-1">{{ t('analysisPage.energyDependentModel') }}</p>
                <div class="d-flex flex-column ga-1">
                  <div class="d-flex justify-space-between">
                    <span>2023:</span>
                    <span class="font-weight-bold">{{ community.mpmMonth2023 }} Months ({{ (community.mpmMonth2023/12).toFixed(2) }} Years)</span>
                  </div>
                  <div class="d-flex justify-space-between">
                    <span>2024:</span>
                    <span class="font-weight-bold">{{ community.mpmMonth2024 }} Months ({{ (community.mpmMonth2024/12).toFixed(2) }} Years)</span>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <v-expansion-panels class="mt-4" multiple>
            <v-expansion-panel>
              <v-expansion-panel-title class="font-weight-bold">{{ t('analysisPage.flatRateModelDetails') }} (2024-2025)</v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-table density="compact">
                  <thead>
                  <tr>
                    <th>{{ t('period') }}</th>
                    <th class="text-center">{{ t('active') }}</th>
                    <th class="text-center">{{ t('activated') }}</th>
                    <th class="text-center">{{ t('deactivated') }}</th>
                    <th class="text-center">{{ t('overlap') }}</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(md, idx) in community.monthData" :key="idx">
                    <td>{{ md.timespan }}</td>
                    <td class="text-center font-weight-bold text-primary">{{ md.countMpmActive }}</td>
                    <td class="text-center text-success">{{ md.countMpmActivated }}</td>
                    <td class="text-center text-warning">{{ md.countMpmDeactivated }}</td>
                    <td class="text-center" :class="{'text-error font-weight-bold': md.countMpmActiveDouble > 0}">{{ md.countMpmActiveDouble }}</td>
                  </tr>
                  </tbody>
                </v-table>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel v-if="community.mpms2024.length > 0">
              <v-expansion-panel-title class="font-weight-bold">
                {{ t('energyDependentModel') }}: {{ t('memberships') }} 2024
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-list density="compact">
                  <v-list-item v-for="(mpm, idx) in community.mpms2024" :key="idx" class="border-bottom py-2">
                    <v-list-item-title>
                      <span class="font-weight-bold">{{ idx + 1 }}. {{ mpm.meteringPoint }}</span>
                      <v-chip size="x-small" class="ml-2" :color="mpm.meteringPointDirection === 'GENERATION' ? 'success' : 'primary'">
                        {{ mpm.meteringPointDirection }}
                      </v-chip>
                    </v-list-item-title>
                    <v-list-item-subtitle class="mt-1">
                      {{ mpm.nameFriendly || '-' }} |
                      {{ new Date(mpm.dateStart).toLocaleDateString(localeToUse) }} &gt;&gt;
                      {{ mpm.dateEnd ? new Date(mpm.dateEnd).toLocaleDateString(localeToUse) : '...' }} |
                      <span class="font-weight-bold text-primary">{{ mpm.countMonth }} Months</span>
                    </v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </template>
    </v-container>
  </v-container>
</template>

<style scoped>
</style>
